import {
  FormikErrorMessage,
  Modal,
  Row,
  Section,
  Button,
  TextInput
} from 'shared'
import { Formik, Form, FormikHelpers, FormikProps } from 'formik'
import { Panel, Col, ButtonToolbar, SelectPicker, Toggle } from 'rsuite'
import { notifyError, notifySuccess } from 'utils'

import {
  defaultRuleConfigFormValues,
  RULE_CONFIG_FORM_FIELDS,
  RuleConfigForm,
  getRuleConfigValidationSchema
} from '../utils'
import {
  useGetFieldMappingListQuery,
  useAddRuleConfigMutation
} from '../clientApiSlice'
import { RuleConfig } from '../types'

interface RuleConfigProps {
  open: boolean;
  onClose: () => void;
  isEditMode?: boolean;
  client: any;
}
const RuleConfigration: React.FC<RuleConfigProps> = ({
  open,
  onClose,
  isEditMode,
  client
}) => {
  const id: number = client?.id ?? 0
  const { data } = useGetFieldMappingListQuery({})
  const [createRuleConfig] = useAddRuleConfigMutation()
  const { RULENAME, OBJECTNAME, ACTIVE } = RULE_CONFIG_FORM_FIELDS

  const initialValues = defaultRuleConfigFormValues
  const dropdownOptions = data?.content.map((rule) => ({
    label: rule.ruleName,
    value: rule.ruleName
  }))

  const onSubmit = async (
    formValues: RuleConfigForm,
    { setSubmitting }: FormikHelpers<RuleConfigForm>
  ) => {
    const ruleConfigDTO: RuleConfig = {
      clientId: id,
      objectName: formValues.objectName,
      ruleName: formValues.ruleName,
      active: formValues.active
    }
    try {
      await createRuleConfig({ addRuleConfigDTO: ruleConfigDTO }).unwrap()
      notifySuccess('Rule configuration created successfully!')

      onClose()
    } catch (error) {
      notifyError('Failed to save rule configuration', {
        containerId: 'modalToast'
      })
    } finally {
      setSubmitting(false)
    }
  }
  const renderFormButtons = (formikProps: FormikProps<RuleConfigForm>) => (
    <ButtonToolbar>
      {isEditMode ? (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save changes
          </Button>
        </>
      ) : (
        <>
          <Button
            className="secondary"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="primary"
            type="submit"
            disabled={formikProps.isValidating || formikProps.isSubmitting}
          >
            Save
          </Button>
        </>
      )}
    </ButtonToolbar>
  )

  return (
    <Modal
      secondary
      open={open}
      onClose={onClose}
      title={isEditMode ? 'Edit Rule Configuration' : 'Add Rule Configuration'}
      size="calc(50% - 120px)"
      body={
        <Formik
          initialValues={initialValues}
          validationSchema={getRuleConfigValidationSchema}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {(formikProps: FormikProps<RuleConfigForm>) => (
            <Form className="create-edit">
              <Panel>
                <Section title="Rule Deatails">
                  <Row>
                    <Col xs={12}>
                      <SelectPicker
                        searchable={false}
                        data={dropdownOptions || []}
                        value={
                          formikProps.values[RULE_CONFIG_FORM_FIELDS.RULENAME]
                        }
                        onChange={(value) => {
                          formikProps.setFieldValue(
                            RULE_CONFIG_FORM_FIELDS.RULENAME,
                            value
                          )
                        }}
                        placeholder="Select Rule"
                        block
                      />

                      <FormikErrorMessage name={RULENAME} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={RULE_CONFIG_FORM_FIELDS.OBJECTNAME}
                        placeholder="Enter Object Name"
                        dataType="string"
                      />
                      <FormikErrorMessage name={OBJECTNAME} />
                    </Col>
                  </Row>
                </Section>
                <Section title="Status">
                  <Row>
                    <Col xs={24} md={12}>
                      <Toggle
                        checked={formikProps.values[ACTIVE]}
                        onChange={() =>
                          formikProps.setFieldValue(
                            ACTIVE,
                            !formikProps.values[ACTIVE]
                          )
                        }
                        data-testid="toggle-active"
                      />
                      <span className="toogle-text">Set as Active</span>
                      <FormikErrorMessage name={ACTIVE} />
                    </Col>
                  </Row>
                </Section>
              </Panel>
              <Panel className="panel-buttons">
                {renderFormButtons(formikProps)}
              </Panel>
            </Form>
          )}
        </Formik>
      }
    />
  )
}

export default RuleConfigration
